/**
 * 打刻した場所（緯度・経度）を、人が読める住所（都道府県・市区町村・町丁目）にする。
 *
 * 緯度・経度の数字は、見ても場所が分からない。国内の主な勤怠システムと同じく、
 * 住所で見せる（正確な位置は［地図を表示］で確かめる）。
 *
 * 変換は国土地理院の公開の仕組みを使う。町丁目の名前と市区町村コードが返るので、
 * コードは同梱の表（src/data/gsiMunicipalities.json。国土地理院の一覧から作ったもの）で
 * 都道府県・市区町村の名前に直す。
 *
 * - **必ず答えを返す（失敗を投げない）。**変換できなければ空文字。打刻履歴はそのまま開く。
 * - 送るのは緯度・経度だけ。**誰の打刻か・いつの打刻かは送らない**（アプリの認証の情報も付けない）。
 * - 同じ場所は1回しか問い合わせない（出勤・退勤・休憩を同じ席で打つのが普通）。
 * - 海外・海上などは、町丁目も市区町村も返らないので空文字。
 */

const ENDPOINT = 'https://mreversegeocoder.gsi.go.jp/reverse-geocoder/LonLatToAddress'
const TIMEOUT_MS = 6000

const cache = new Map()
let municipalities = null

const loadMunicipalities = () => {
  if (!municipalities) {
    // 表は 60KB ほどある。打刻履歴を開いて、場所のある記録があったときにだけ読む。
    municipalities = import(/* webpackChunkName: "gsi-municipalities" */ '../data/gsiMunicipalities.json')
      .then(module => module.default || module)
      .catch(() => ({}))
  }
  return municipalities
}

const keyOf = (latitude, longitude) => latitude.toFixed(5) + ',' + longitude.toFixed(5)

/** 応答（町丁目の名前と市区町村コード）と表から、住所の文字を作る。作れなければ空文字。 */
export const placeNameFrom = (results, table) => {
  if (!results || typeof results !== 'object') return ''
  const code = results.muniCd == null ? '' : String(Number(results.muniCd))
  const municipality = (table && code && table[code]) || ''
  if (!municipality) return ''
  const town = typeof results.lv01Nm === 'string' ? results.lv01Nm.trim() : ''
  // 町丁目が無い所（「－」で返る）は、市区町村まで。
  return town && town !== '－' && town !== '-' ? municipality + town : municipality
}

/**
 * @param {*} latitude
 * @param {*} longitude
 * @param {object} [options] 試験用（fetchImpl・table）
 * @returns {Promise<string>} 住所。変換できなければ空文字
 */
export const stampPlaceName = (latitude, longitude, options = {}) => {
  const lat = Number(latitude)
  const lon = Number(longitude)
  if (latitude == null || longitude == null || !isFinite(lat) || !isFinite(lon)) {
    return Promise.resolve('')
  }
  const key = keyOf(lat, lon)
  if (!options.fetchImpl && cache.has(key)) return cache.get(key)

  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null)
  if (!fetchImpl) return Promise.resolve('')

  const table = options.table ? Promise.resolve(options.table) : loadMunicipalities()
  const controller = typeof AbortController === 'function' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), TIMEOUT_MS) : null

  const answer = Promise.resolve()
    .then(() => fetchImpl(ENDPOINT + '?lat=' + lat + '&lon=' + lon, {
      method: 'GET',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      signal: controller ? controller.signal : undefined
    }))
    .then(response => (response && response.ok ? response.json() : null))
    .then(body => table.then(names => placeNameFrom(body && body.results, names)))
    .catch(() => '')
    .then(name => {
      if (timer) clearTimeout(timer)
      // 変換できなかった場所は覚えない（通信が戻ったら、開き直したときにもう一度試す）。
      if (!name) cache.delete(key)
      return name
    })

  if (!options.fetchImpl) cache.set(key, answer)
  return answer
}
